import type { ReactNode } from 'react';
import type { CatalogV2ListRow } from '@skupreme/api-contracts/core';
import {
  Button,
  Popover,
  SearchField,
  Select,
  FilterButton,
} from '@skupreme/shared/ui';
import { useActiveScope } from '@features/account-view/active';
import type { ISortObject } from '../../legacy-bridge';
import { useConnections } from '@features/rebuild-settings/use-rebuild-settings';
import { useSeenChannels } from '../../hooks/use-seen-channels';
import {
  defaultCatalogV2Filter,
  type CatalogV2Filter,
} from '../../lib/list-params';
import { fromSortDescriptor } from '../../lib/table';
import {
  ALL,
  channelLabel,
  DEFAULT_ACTIVITY,
  V2_ACTIVITY_OPTIONS,
  V2_STATE_OPTIONS,
  V2_TYPE_OPTIONS,
} from '../../lib/v2';
import { CatalogDivisionFilter } from './CatalogDivisionFilter';

const SORT_OPTIONS = [
  { value: ALL, label: 'Default order' },
  { value: 'title', label: 'Title' },
  { value: 'sku', label: 'SKU' },
  { value: 'available', label: 'Available units' },
];

const ORDER_OPTIONS = [
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
];

/* Wide enough for a channel name with its division ("Amazon North America · Hawk"). */
const TRIGGER_WIDTH = 'sk:w-60';

export type CatalogV2FiltersProps = {
  rows: CatalogV2ListRow[];
  search: string;
  onSearch: (search: string) => void;
  filter: CatalogV2Filter;
  onFilterChange: (key: keyof CatalogV2Filter, value: string) => void;
  sort: ISortObject;
  onSortChange: (sort: ISortObject) => void;
  /** The division filter changed (account level only). */
  onDivisionChange?: () => void;
  /** M2: division id -> the one country it sells in, for the division options' flags. */
  divisionCountries?: ReadonlyMap<string, string>;
};

/* One control in the popover, named on the left like the orders filters. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="sk:flex sk:items-center sk:justify-between sk:gap-3 sk:px-3 sk:py-2">
      <span className="sk:text-dense sk:font-medium sk:text-fg">{label}</span>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="sk:my-1 sk:border-t sk:border-border-subtle" />;
}

/**
 * Search, then a Filters popover holding state, listings (active or not), type, channel, division and
 * sort: the V2 list's controls on the section tabs' row. Behind one button so
 * the row keeps room for the tabs at the account level, where the division
 * filter joins them.
 */
export function CatalogV2Filters({
  rows,
  search,
  onSearch,
  filter,
  onFilterChange,
  sort,
  onSortChange,
  onDivisionChange,
  divisionCountries,
}: CatalogV2FiltersProps) {
  const [sortBy, order] = Object.entries(sort)[0] ?? [ALL, 'asc'];
  const { level, divisions, divisionFilter, setDivisionFilter } =
    useActiveScope();
  const isAccount = level === 'account';
  /* Every sales channel connected, so one with no listing on these pages yet can be picked too. */
  const { data: connections } = useConnections(
    isAccount ? 'account' : 'division',
  );
  const divisionName = (id: string) =>
    divisions.find(division => division.id === id)?.name ?? id;
  const channels = useSeenChannels(
    rows,
    connections?.v2
      ? connections.connections
          .filter(connection => connection.kind === 'marketplace')
          .map(connection => ({
            id: connection.id,
            label: channelLabel({
              provider: connection.provider,
              display_name: connection.name,
            }),
            divisions: connection.divisionIds.map(divisionName),
          }))
      : [],
  );

  /* Sorting is not a narrowing, so it does not count towards the badge. */
  const active =
    (filter.state !== defaultCatalogV2Filter.state ? 1 : 0) +
    (filter.activity ? 1 : 0) +
    (filter.type ? 1 : 0) +
    (filter.connectionId ? 1 : 0) +
    (isAccount && divisionFilter.length > 0 ? 1 : 0);

  const clearAll = () => {
    onFilterChange('state', defaultCatalogV2Filter.state);
    onFilterChange('activity', '');
    onFilterChange('type', '');
    onFilterChange('connectionId', '');

    if (isAccount && divisionFilter.length > 0) {
      setDivisionFilter([]);
      onDivisionChange?.();
    }
  };

  return (
    <>
      <SearchField
        aria-label="Search catalog"
        placeholder="Search title, SKU, GTIN or ASIN"
        value={search}
        onChange={onSearch}
        className="sk:w-full sk:sm:w-72"
      />
      <Popover
        aria-label="Filters"
        placement="bottom end"
        trigger={<FilterButton count={active} />}
      >
        <div className="sk:flex sk:w-80 sk:flex-col">
          <div className="sk:flex sk:items-center sk:justify-between sk:gap-6 sk:border-b sk:border-border-subtle sk:px-3 sk:py-2">
            <span className="sk:text-xs sk:font-semibold sk:text-fg-tertiary">
              Filters
            </span>
            {active > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                className="sk:text-dense"
                onPress={clearAll}
              >
                Clear all
              </Button>
            ) : null}
          </div>

          <Row label="State">
            <Select
              aria-label="State"
              value={filter.state}
              options={V2_STATE_OPTIONS}
              onChange={value => onFilterChange('state', value)}
              className={TRIGGER_WIDTH}
            />
          </Row>
          <Row label="Listings">
            <Select
              aria-label="Listings"
              value={filter.activity || DEFAULT_ACTIVITY}
              options={V2_ACTIVITY_OPTIONS}
              onChange={value =>
                onFilterChange(
                  'activity',
                  value === DEFAULT_ACTIVITY ? '' : value,
                )
              }
              className={TRIGGER_WIDTH}
            />
          </Row>
          <Row label="Type">
            <Select
              aria-label="Type"
              value={filter.type || ALL}
              options={V2_TYPE_OPTIONS}
              onChange={value =>
                onFilterChange('type', value === ALL ? '' : value)
              }
              className={TRIGGER_WIDTH}
            />
          </Row>
          <Row label="Channel">
            <Select
              aria-label="Channel"
              value={filter.connectionId || ALL}
              options={[{ value: ALL, label: 'All channels' }, ...channels]}
              onChange={value =>
                onFilterChange('connectionId', value === ALL ? '' : value)
              }
              className={TRIGGER_WIDTH}
            />
          </Row>
          {isAccount ? (
            <Row label="Division">
              <CatalogDivisionFilter
                onChange={onDivisionChange}
                className={TRIGGER_WIDTH}
                divisionCountries={divisionCountries}
              />
            </Row>
          ) : null}

          <Divider />

          <Row label="Sort by">
            <Select
              aria-label="Sort by"
              value={sortBy}
              options={SORT_OPTIONS}
              onChange={value =>
                onSortChange(
                  value === ALL
                    ? {}
                    : fromSortDescriptor(
                        { column: value, direction: 'ascending' },
                        {},
                      ),
                )
              }
              className={TRIGGER_WIDTH}
            />
          </Row>
          <Row label="Order">
            <Select
              aria-label="Sort order"
              value={order}
              options={ORDER_OPTIONS}
              isDisabled={sortBy === ALL}
              onChange={value =>
                onSortChange({ [sortBy]: value === 'desc' ? 'desc' : 'asc' })
              }
              className={TRIGGER_WIDTH}
            />
          </Row>
        </div>
      </Popover>
    </>
  );
}
